import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Col, Form, Modal, Pagination, Row } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import Select, { components } from "react-select";
import { toast } from "react-toastify";
import api from "../api/client";
import EmptyState from "../components/common/EmptyState";
import LoadingState from "../components/common/LoadingState";
import PageHeader from "../components/common/PageHeader";
import { useAuth } from "../context/AuthContext";
import { loadActivityLookups } from "../utils/activityLookups";
import { getWorkplanYears } from "../utils/workplanYears";
import { downloadApiFile } from "../utils/download";

const statusOptions = ["In Progress", "Completed", "On Hold", "Not Done"];
const quarterOptions = ["Q1", "Q2", "Q3", "Q4"].map((quarter) => ({ value: quarter, label: quarter }));
const PAGE_SIZE = 20;
const ACTIVITY_LIST_STATE_KEY = "activity-list-state";

function defaultActivityFilters(currentYear) {
  return {
    search: "",
    year: currentYear,
    quarters: [],
    status: "",
    pillar_ids: [],
    implementor_ids: [],
    grant_ids: []
  };
}

function loadActivityListState(currentYear) {
  const fallback = {
    filters: defaultActivityFilters(currentYear),
    currentPage: 1
  };

  try {
    const saved = JSON.parse(window.sessionStorage.getItem(ACTIVITY_LIST_STATE_KEY));
    if (!saved || typeof saved !== "object") {
      return fallback;
    }

    return {
      filters: {
        search: typeof saved.filters?.search === "string" ? saved.filters.search : "",
        year: Number(saved.filters?.year) || currentYear,
        quarters: Array.isArray(saved.filters?.quarters) ? saved.filters.quarters : [],
        status: statusOptions.includes(saved.filters?.status) ? saved.filters.status : "",
        pillar_ids: Array.isArray(saved.filters?.pillar_ids) ? saved.filters.pillar_ids : [],
        implementor_ids: Array.isArray(saved.filters?.implementor_ids) ? saved.filters.implementor_ids : [],
        grant_ids: Array.isArray(saved.filters?.grant_ids) ? saved.filters.grant_ids : []
      },
      currentPage: Number(saved.currentPage) > 0 ? Number(saved.currentPage) : 1
    };
  } catch (error) {
    return fallback;
  }
}

function CheckboxOption({ children, isSelected, ...props }) {
  return (
    <components.Option {...props} isSelected={isSelected}>
      <span className="d-flex align-items-center gap-2">
        <input type="checkbox" checked={isSelected} readOnly tabIndex={-1} aria-hidden="true" />
        <span>{children}</span>
      </span>
    </components.Option>
  );
}

const multiSelectComponents = { Option: CheckboxOption };
const multiSelectStyles = {
  menu: (base) => ({
    ...base,
    zIndex: 100
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "#eef6fc" : "#ffffff",
    color: "#17324d",
    ":active": {
      backgroundColor: "#e2eff8"
    }
  }),
  valueContainer: (base) => ({
    ...base,
    alignItems: "center",
    columnGap: 4,
    rowGap: 2
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "transparent",
    color: "#17324d",
    margin: 0,
    maxWidth: "100%"
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#17324d",
    fontWeight: 600,
    paddingLeft: 3,
    paddingRight: 2,
    overflow: "visible",
    textOverflow: "clip"
  }),
  multiValueRemove: (base) => ({
    ...base,
    backgroundColor: "transparent",
    ":hover": {
      backgroundColor: "transparent",
      color: "#be123c"
    }
  })
};

function ActivitiesPage() {
  const history = useHistory();
  const { user } = useAuth();
  const currentYear = new Date().getFullYear();
  const initialListState = useMemo(() => loadActivityListState(currentYear), [currentYear]);
  const availableYears = getWorkplanYears(currentYear);
  const [activities, setActivities] = useState([]);
  const [grants, setGrants] = useState([]);
  const [implementors, setImplementors] = useState([]);
  const [pillars, setPillars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(initialListState.currentPage);
  const [totalActivities, setTotalActivities] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [activeUpdatesActivity, setActiveUpdatesActivity] = useState(null);
  const [quarterUpdates, setQuarterUpdates] = useState([]);
  const [quarterUpdatesLoading, setQuarterUpdatesLoading] = useState(false);
  const [quarterUpdatesError, setQuarterUpdatesError] = useState("");
  const [editingUpdate, setEditingUpdate] = useState(null);
  const [editUpdateForm, setEditUpdateForm] = useState({ content: "", saving: false });
  const [downloading, setDownloading] = useState(false);
  const [filters, setFilters] = useState(initialListState.filters);
  const [searchInput, setSearchInput] = useState(initialListState.filters.search);
  const filtersMounted = useRef(false);

  useEffect(() => {
    fetchLookups();
  }, []);

  const implementorNameById = useMemo(
    () =>
      implementors.reduce((accumulator, implementor) => {
        accumulator[String(implementor.id)] = implementor.name;
        return accumulator;
      }, {}),
    [implementors]
  );

  const grantNameById = useMemo(
    () =>
      grants.reduce((accumulator, grant) => {
        accumulator[String(grant.id)] = grant.name;
        return accumulator;
      }, {}),
    [grants]
  );

  useEffect(() => {
    if (!filtersMounted.current) {
      filtersMounted.current = true;
      return;
    }
    setCurrentPage(1);
  }, [filters.search, filters.year, filters.quarters, filters.status, filters.pillar_ids, filters.implementor_ids, filters.grant_ids]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      const search = searchInput.trim();
      setFilters((current) => (current.search === search ? current : { ...current, search }));
    }, 350);

    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        ACTIVITY_LIST_STATE_KEY,
        JSON.stringify({ filters, currentPage })
      );
    } catch (error) {
      // Storage can be unavailable in privacy-restricted browsers. The filters
      // still work for the current page even when they cannot be persisted.
    }
  }, [filters, currentPage]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  async function fetchActivities() {
    setLoading(true);
    setError("");

    try {
      const params = {};

      if (filters.search) {
        params.search = filters.search;
      }
      if (filters.year) {
        params.year = filters.year;
      }
      if (filters.status) {
        params.status = filters.status;
      }
      if (filters.quarters.length) {
        params.quarter = filters.quarters.join(",");
      }
      if (filters.pillar_ids.length) {
        params.pillar_id = filters.pillar_ids.join(",");
      }
      if (filters.implementor_ids.length) {
        params.implementor_id = filters.implementor_ids.join(",");
      }
      if (filters.grant_ids.length) {
        params.grant_id = filters.grant_ids.join(",");
      }
      params.page = currentPage;
      params.per_page = PAGE_SIZE;

      const response = await api.get("/activities", { params });
      setActivities(response.data?.items || []);
      setTotalActivities(response.data?.total || 0);
      setTotalPages(response.data?.total_pages || 0);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load activities.");
      setActivities([]);
      setTotalActivities(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchActivities();
  }, [filters.search, filters.year, filters.quarters, filters.status, filters.pillar_ids, filters.implementor_ids, filters.grant_ids, currentPage]);

  async function fetchLookups() {
    const lookupData = await loadActivityLookups();
    setGrants(lookupData.grants);
    setImplementors(lookupData.implementors);
    setPillars(lookupData.pillars);
  }

  function handleFilterChange(event) {
    const { name, value } = event.target;
    setFilters((current) => ({
      ...current,
      [name]: value
    }));
  }

  function handleMultiFilterChange(name, selectedOptions) {
    setFilters((current) => ({
      ...current,
      [name]: (selectedOptions || []).map((option) => option.value)
    }));
  }

  function handleClearFilters() {
    setCurrentPage(1);
    setSearchInput("");
    setFilters(defaultActivityFilters(currentYear));
  }

  async function handleDownload() {
    setDownloading(true);

    try {
      await downloadApiFile("/activities/export/workplan", {
        params: {
          search: filters.search || undefined,
          year: filters.year,
          quarter: filters.quarters.length ? filters.quarters.join(",") : undefined,
          status: filters.status || undefined,
          pillar_id: filters.pillar_ids.length ? filters.pillar_ids.join(",") : undefined,
          implementor_id: filters.implementor_ids.length ? filters.implementor_ids.join(",") : undefined,
          grant_id: filters.grant_ids.length ? filters.grant_ids.join(",") : undefined
        },
        fallbackFileName: `activities-${filters.year}.csv`
      });
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to download activities export.");
    } finally {
      setDownloading(false);
    }
  }

  function renderPaginationControls(position) {
    if (totalPages <= 1) {
      return (
        <div className={`activity-pagination-bar ${position}`}>
          <div className="activity-total-count">Total activities: {totalActivities}</div>
        </div>
      );
    }

    return (
      <div className={`activity-pagination-bar ${position}`}>
        <div className="activity-total-count">
          Total activities: {totalActivities}
          <span className="text-muted ms-2">
            Showing {(currentPage - 1) * PAGE_SIZE + 1}-{Math.min(currentPage * PAGE_SIZE, totalActivities)}
          </span>
        </div>
        <Pagination className="mb-0 app-pagination">
          <Pagination.Prev onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={currentPage === 1}>
            Previous
          </Pagination.Prev>
          <Pagination.Item active disabled className="app-pagination-status">
            Page {currentPage} of {totalPages}
          </Pagination.Item>
          <Pagination.Next
            onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </Pagination.Next>
        </Pagination>
      </div>
    );
  }

  async function openUpdatesModal(activity) {
    setActiveUpdatesActivity(activity);
    setQuarterUpdates([]);
    setQuarterUpdatesError("");
    setQuarterUpdatesLoading(true);

    try {
      const response = await api.get(`/activities/${activity.uuid}/quarter-updates`);
      setQuarterUpdates(response.data || []);
    } catch (err) {
      setQuarterUpdatesError(err.response?.data?.error || "Failed to load activity updates.");
    } finally {
      setQuarterUpdatesLoading(false);
    }
  }

  function closeUpdatesModal() {
    setActiveUpdatesActivity(null);
    setQuarterUpdates([]);
    setQuarterUpdatesError("");
    setQuarterUpdatesLoading(false);
    setEditingUpdate(null);
    setEditUpdateForm({ content: "", saving: false });
  }

  function canEditUpdate(update) {
    return Boolean(user?.uuid && update?.user_uuid && user.uuid === update.user_uuid);
  }

  function openEditUpdateModal(update) {
    setEditingUpdate(update);
    setEditUpdateForm({ content: update.content || "", saving: false });
  }

  function closeEditUpdateModal() {
    setEditingUpdate(null);
    setEditUpdateForm({ content: "", saving: false });
  }

  async function handleEditUpdateSave() {
    if (!activeUpdatesActivity || !editingUpdate) {
      return;
    }
    if (!editUpdateForm.content.trim()) {
      toast.error("Comment is required.");
      return;
    }

    setEditUpdateForm((current) => ({ ...current, saving: true }));

    try {
      await api.put(
        `/activities/${activeUpdatesActivity.uuid}/quarter-updates/${editingUpdate.uuid}`,
        {
          year: Number(editingUpdate.year),
          quarter: editingUpdate.quarter,
          status: editingUpdate.status,
          content: editUpdateForm.content.trim()
        }
      );
      const response = await api.get(`/activities/${activeUpdatesActivity.uuid}/quarter-updates`);
      setQuarterUpdates(response.data || []);
      toast.success("Comment updated");
      closeEditUpdateModal();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update comment.");
      setEditUpdateForm((current) => ({ ...current, saving: false }));
    }
  }

  return (
    <div>
      <PageHeader
        title="Activities"
        action={
          <div className="d-flex gap-2">
            <Button variant="primary" onClick={() => history.push("/activities/new")}>
              Add activity
            </Button>
            <Button variant="outline-primary" onClick={handleDownload} disabled={downloading || loading} title="Download activities">
              {downloading ? "Downloading..." : "Download"}
            </Button>
            <Button variant="outline-secondary" onClick={handleClearFilters}>
              Clear filters
            </Button>
          </div>
        }
      />

      <Row className="g-4 mb-4">
        <Col xs={12}>
          <div className="content-card table-card">
            <Row className="activity-filter-grid g-3 mb-3">
              <Col xs={12} className="activity-search-filter">
                <label className="form-label" htmlFor="activity-search">Search activities</label>
                <Form.Control
                  id="activity-search"
                  type="search"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Search by activity name or code"
                />
              </Col>
              <Col md={6} xl={2}>
                <label className="form-label">Filter year</label>
                <Form.Select name="year" value={filters.year} onChange={handleFilterChange}>
                  {availableYears.map((itemYear) => (
                    <option key={itemYear} value={itemYear}>
                      {itemYear}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={6} xl={2}>
                <label className="form-label">Quarter</label>
                <Select
                  inputId="activity-quarter-filter"
                  classNamePrefix="react-select"
                  isMulti
                  controlShouldRenderValue
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  components={multiSelectComponents}
                  styles={multiSelectStyles}
                  options={quarterOptions}
                  value={quarterOptions.filter((option) => filters.quarters.includes(option.value))}
                  onChange={(selected) => handleMultiFilterChange("quarters", selected)}
                  placeholder="All quarters"
                />
              </Col>
              <Col md={6} xl={2}>
                <label className="form-label">Status</label>
                <Form.Select name="status" value={filters.status} onChange={handleFilterChange}>
                  <option value="">All statuses</option>
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={6} xl={2}>
                <label className="form-label">Pillar</label>
                <Select
                  inputId="activity-pillar-filter"
                  classNamePrefix="react-select"
                  isMulti
                  controlShouldRenderValue
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  components={multiSelectComponents}
                  styles={multiSelectStyles}
                  options={pillars.map((pillar) => ({ value: String(pillar.id), label: pillar.name }))}
                  value={pillars
                    .filter((pillar) => filters.pillar_ids.includes(String(pillar.id)))
                    .map((pillar) => ({ value: String(pillar.id), label: pillar.name }))}
                  onChange={(selected) => handleMultiFilterChange("pillar_ids", selected)}
                  placeholder="All pillars"
                />
              </Col>
              <Col md={6} xl={2}>
                <label className="form-label">Implementor</label>
                <Select
                  inputId="activity-implementor-filter"
                  classNamePrefix="react-select"
                  isMulti
                  controlShouldRenderValue
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  components={multiSelectComponents}
                  styles={multiSelectStyles}
                  options={implementors.map((implementor) => ({ value: String(implementor.id), label: implementor.name }))}
                  value={implementors
                    .filter((implementor) => filters.implementor_ids.includes(String(implementor.id)))
                    .map((implementor) => ({ value: String(implementor.id), label: implementor.name }))}
                  onChange={(selected) => handleMultiFilterChange("implementor_ids", selected)}
                  placeholder="All implementors"
                />
              </Col>
              <Col md={6} xl={2}>
                <label className="form-label">Grant</label>
                <Select
                  inputId="activity-grant-filter"
                  classNamePrefix="react-select"
                  isMulti
                  controlShouldRenderValue
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  components={multiSelectComponents}
                  styles={multiSelectStyles}
                  options={grants.map((grant) => ({ value: String(grant.id), label: grant.name }))}
                  value={grants
                    .filter((grant) => filters.grant_ids.includes(String(grant.id)))
                    .map((grant) => ({ value: String(grant.id), label: grant.name }))}
                  onChange={(selected) => handleMultiFilterChange("grant_ids", selected)}
                  placeholder="All grants"
                />
              </Col>
            </Row>

            {loading ? <LoadingState label="Loading activities..." /> : null}
            {error ? <Alert variant="danger">{error}</Alert> : null}
            {!loading && !error && !activities.length ? (
              <EmptyState title="No activities yet" text="Create your first activity from the add activity page." />
            ) : null}

            {!loading && activities.length ? (
              <>
                {renderPaginationControls("top")}
                <div className="table-responsive">
                  <table className="table align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Name</th>
                        <th>Implementor</th>
                        <th>Grant</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activities.map((activity) => (
                        <tr key={activity.uuid}>
                          <td className="activity-code-cell">
                            <span className="badge-soft activity-code-badge">{activity.code}</span>
                          </td>
                          <td className="activity-name-cell">
                            <button
                              type="button"
                              className="activity-link"
                              onClick={() => history.push(`/activities/${activity.uuid}`)}
                            >
                              {activity.name}
                            </button>
                            <small className="text-muted">{activity.level || "No level"}</small>
                          </td>
                          <td>
                            {implementorNameById[String(activity.implementor_id || "")] ||
                              (activity.implementor_id ? `Implementor #${activity.implementor_id}` : (
                                <span className="text-muted">No implementor</span>
                              ))}
                          </td>
                          <td>
                            {grantNameById[String(activity.grant_id || "")] ||
                              (activity.grant_id ? `Grant #${activity.grant_id}` : (
                                <span className="text-muted">No grant</span>
                              ))}
                          </td>
                          <td className="text-end">
                            <div className="d-flex justify-content-end">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                disabled={!activity.quarter_update_count}
                                onClick={() => openUpdatesModal(activity)}
                              >
                                {activity.quarter_update_count ? "View updates" : "No Updates"}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {renderPaginationControls("bottom")}
              </>
            ) : null}
          </div>
        </Col>
      </Row>

      <Modal show={Boolean(activeUpdatesActivity)} onHide={closeUpdatesModal} centered size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            {activeUpdatesActivity ? `${activeUpdatesActivity.code} quarter updates` : "Quarter updates"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeUpdatesActivity ? (
            <div className="d-flex flex-column gap-3">
              <div className="small text-muted">{activeUpdatesActivity.name}</div>

              {quarterUpdatesLoading ? <LoadingState label="Loading quarter updates..." /> : null}
              {quarterUpdatesError ? <Alert variant="danger" className="mb-0">{quarterUpdatesError}</Alert> : null}

              {!quarterUpdatesLoading && !quarterUpdatesError && !quarterUpdates.length ? (
                <p className="mb-0 text-muted">No quarter updates have been added for this activity yet.</p>
              ) : null}

              {!quarterUpdatesLoading && !quarterUpdatesError && quarterUpdates.length ? (
                <div className="table-responsive">
                  <table className="table align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Quarter</th>
                        <th>Year</th>
                        <th>Status</th>
                        <th>Comment</th>
                        <th>Updated By</th>
                        <th>Updated On</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quarterUpdates.map((update) => (
                        <tr key={update.uuid}>
                          <td>{update.quarter || "-"}</td>
                          <td>{update.year || "-"}</td>
                          <td>
                            <span className="activity-quarter-table-status">{update.status || "-"}</span>
                          </td>
                          <td>{update.content || "-"}</td>
                          <td>
                            {[update.firstname, update.lastname].filter(Boolean).join(" ") || update.username || "-"}
                          </td>
                          <td>{update.created_at ? new Date(update.created_at).toLocaleDateString() : "-"}</td>
                          <td className="text-end">
                            {canEditUpdate(update) ? (
                              <Button variant="outline-primary" size="sm" onClick={() => openEditUpdateModal(update)}>
                                Edit
                              </Button>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeUpdatesModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(editingUpdate)} onHide={closeEditUpdateModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit comment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingUpdate ? (
            <div className="d-flex flex-column gap-3">
              <div className="small text-muted">{editingUpdate.quarter} {editingUpdate.year}</div>
              <div>
                <label className="form-label" htmlFor="activity-update-comment">Comment</label>
                <Form.Control
                  id="activity-update-comment"
                  as="textarea"
                  rows={6}
                  value={editUpdateForm.content}
                  onChange={(event) => setEditUpdateForm((current) => ({ ...current, content: event.target.value }))}
                />
              </div>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeEditUpdateModal}>Cancel</Button>
          <Button onClick={handleEditUpdateSave} disabled={editUpdateForm.saving}>
            {editUpdateForm.saving ? "Saving..." : "Save changes"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ActivitiesPage;
